declare var jQuery: any;

import { Config, ProductType } from "./types";
import { CalculatorOnsite } from "./calculator";
import { Product } from "./product";
import { WidgetLeaseLinkButton } from "./button";

export class LeaseLinkWidget {
  static MIN_VALUE = 300;
  static MAX_VALUE = 150000;
  static BASE_URL: string = "https://online.leaselink.pl";
  private token: string | null = null;
  private isCartReadOnly: boolean = true;

  config: Config;
  calculator: CalculatorOnsite | null = null;

  products: Product[] = [];
  
  // Calculate Button
  private calculateButton: WidgetLeaseLinkButton | null = null;
  private priceObserver: MutationObserver | null = null;

  get apiUrl(): string {
    return this.config?.apiUrl || LeaseLinkWidget.BASE_URL;
  }

  get isSandbox(): boolean {
    return !!this.config?.isSandbox;
  }

  get isProductPage(): boolean {
    return !!this.config?.isProductPage;
  }

  get isCheckout(): boolean {
    return !!this.config?.isCheckout;
  }

  get isCart(): boolean {
    return !!this.config?.isCart;
  }

  get showRate(): boolean {
    return !!this.config?.buttonShowRate;
  }

  constructor(config: Config) {
    if (!config) throw new Error("LeaseLinkWidget: config is required");
    this.config = config;
    this.init();
  }

  async init() {
    // Initialize Calculate Button if enabled on product page
    if (this.config.calculateButtonEnabled && this.isProductPage) {
      await this.initCalculateButton();
      return; // Calculate Button and Calculator On-Site are mutually exclusive
    }

    // Initialize Calculator On-Site (only when calculate button is disabled)
    this.calculator = new CalculatorOnsite(this.config, this);
    
    if (
      this.isProductPage &&
      this.config.productId &&
      this.config.productType !== ProductType.VARIABLE
    ) {
      const should = await this.shouldShowCalculator();
      await this.updateCalculatorVisibility(should);
    }

    if (
      this.isProductPage &&
      this.config.productId &&
      this.config.productType === ProductType.VARIABLE
    ) {
      const should = await this.shouldShowCalculator({ variationId: null });
      await this.updateCalculatorVisibility(should);
    }

    // Initialize on Cart page
    if (this.isCart && this.config.enableCalculatorOnCart) {
      const should = await this.shouldShowCalculator();
      await this.updateCalculatorVisibility(should);
    }

    if (this.isCheckout) {
      const should = await this.shouldShowCalculator();
      await this.updateCalculatorVisibility(should);

      const targetNode = document.querySelector(
        ".wc-block-checkout__payment-method"
      );
      if (targetNode) {
        const config = { childList: true, subtree: true };
        const callback = () => {
          this.shouldShowCalculator().then((should) => {
            this.updateCalculatorVisibility(should);
          });
          observer.disconnect();
        };
        const observer = new MutationObserver(callback);
        observer.observe(targetNode, config);
      }

      // Debounce timer dla aktualizacji kalkulatora
      let updateTimer: number | null = null;
      const debouncedUpdate = () => {
        if (updateTimer !== null) {
          clearTimeout(updateTimer);
        }
        updateTimer = window.setTimeout(async () => {
          const should = await this.shouldShowCalculator();
          await this.updateCalculatorVisibility(should);
          updateTimer = null;
        }, 500);
      };

      // Przechwytywanie Fetch API dla WooCommerce Blocks
      const originalFetch = window.fetch;
      window.fetch = async (...args) => {
        const response = await originalFetch(...args);
        let url = '';
        if (typeof args[0] === 'string') {
          url = args[0];
        } else if (args[0] instanceof Request) {
          url = args[0].url;
        } else if (args[0] instanceof URL) {
          url = args[0].href;
        }
        
        if (url.includes('/wc/store/v1/cart/select-shipping-rate')) {
          debouncedUpdate();
        }
        
        return response;
      };

      // Aktualizacja po zakończeniu /?wc-ajax=update_order_review (klasyczny checkout)
      jQuery(document).ajaxComplete(async (_e: any, _xhr: any, settings: any) => {
        const url = settings?.url ?? "";
        // Klasyczny checkout
        if (typeof url === "string" && url.includes("wc-ajax=update_order_review")) {
          debouncedUpdate();
        }
      });

      // Nasłuchuj na event updated_checkout (WooCommerce standard event)
      jQuery(document.body).on("updated_checkout", () => {
        debouncedUpdate();
      });

      // Nasłuchuj na zmiany metody wysyłki (klasyczny checkout)
      jQuery(document.body).on("change", "input[name^='shipping_method']", () => {
        debouncedUpdate();
      });

    }

    jQuery("form.variations_form").on(
      "show_variation",
      (_: any, variation: any) => void this.handleVariationChange(variation)
    );
    jQuery("form.variations_form").on(
      "hide_variation",
      () => void this.handleVariationChange(null)
    );

    jQuery(document.body).on(
      "change",
      this.getPaymentMethodSelector(),
      async (_event: Event) => {
        const isSelected = this.isLeaseLinkPaymentSelected();
        const should = isSelected ? await this.shouldShowCalculator() : false;
        await this.updateCalculatorVisibility(should);
      }
    );
  }

  private async handleVariationChange(variation: any | null) {
    if (variation) {
      const products = await this.getProductById(variation.variation_id);
      this.products = products;
      const should = await this.shouldShowCalculator({
        variationId: variation.variation_id,
      });
      await this.updateCalculatorVisibility(should);
    } else {
      const should = await this.shouldShowCalculator({ variationId: null });
      await this.updateCalculatorVisibility(should);
    }
  }

  /**
   * Handle variation changes for Calculate Button
   */
  private async handleCalculateButtonVariationChange(variation: any | null) {
    if (variation) {
      const products = await this.getProductById(variation.variation_id);
      this.products = products;
    } else {
      this.products = [];
    }
    await this.updateCalculateButtonUrl();
  }

  private async updateCalculatorVisibility(shouldShow: boolean) {
    try {
      const isSelected = this.isLeaseLinkPaymentSelected();
      if (shouldShow && isSelected) {
        await this.renderCalculator();
        await this.updateButtonText();
      } else {
        this.removeCalculator();
      }
    } catch (e) {
      console.warn("[LeaseLink] Failed to update calculator visibility", e);
    }
  }

  private async updateButtonText() {
    if (!this.calculator) return;
    if (this.showRate && this.products.length > 0) {
      const calculation = await this.createCalculation(this.products, {
        "Component-Name": "dynamic button",
        "Component-Placement": this.isProductPage ? "card" : "payments",
        "External-App": "WordPress",
      });
      this.calculator.updateButtonText(calculation.Offers[0]);
    }
  }

  private async renderCalculator() {
    if (!this.calculator) return;
    
    // ensure calculator element built
    const el = await this.calculator.ensureBuilt();

    // if already in DOM, do nothing
    if (el && el.parentElement) return;

    let parent: Element | null = null;

    if (this.config.isProductPage) {
      const selector =
        this.config.renderToSelector || this.calculator.renderToSelector;

      if (selector) parent = document.querySelector(selector);
    } else if (this.config.isCart && this.config.enableCalculatorOnCart) {
      const selector = this.config.buttonCartSelector || '.leaselink-calculator-cart-container';
      parent = document.querySelector(selector);
    } else if (this.config.isCheckout && this.config.enableCalculatorOnCheckout) {
      const selector = '.leaselink-calculator-checkout-container';
      parent = document.querySelector(selector);
    }

    if (!parent) {
      return;
    }

    // attach as last child
    parent.appendChild(el);
    parent.appendChild(this.calculator.buttonElement);
    // remember parent for removal
    this.calculator.parentElement = parent as HTMLElement;
  }

  private removeCalculator() {
    if (!this.calculator) return;
    
    try {
      const el = this.calculator.calculatorElement;
      if (el && el.parentElement) {
        el.parentElement.removeChild(el);
      }
      this.calculator.parentElement = null;

      const btn = this.calculator.buttonElement;
      if (btn && btn.parentElement) {
        btn.parentElement.removeChild(btn);
      }
    } catch (e) {
      // noop
    }
  }

  private async getProductById(id: number | string): Promise<Product[]> {
    const res = await fetch(`/wp-json/leaselink/v1/product/${id}`, {
      credentials: "same-origin",
    });
    if (!res.ok) throw new Error("Failed to fetch product");
    const data = await res.json();
    if (!Array.isArray(data)) throw new Error("Invalid product data");
    return data.map((p: any) => Product.fromJson(p));
  }

  private async getCartProducts(): Promise<Product[]> {
    const res = await fetch(`/wp-json/leaselink/v1/checkout`, {
      credentials: "same-origin",
    });
    if (!res.ok) {
      throw new Error("Failed to fetch checkout");
    }
    const data = await res.json();
    if (!Array.isArray(data)) {
      throw new Error("Invalid checkout data");
    }
    const products = data.map((p: any) => Product.fromJson(p, p.Quantity || 1));
    return products;
  }

  private isPriceInRange(product: Product) {
    const price = Number(product?.UnitNetPrice || 0);
    
    // Skip minimum price check if:
    // - On-site calculator is enabled on product page
    // - Cart building in calculator is enabled
    // - We are on product page
    const skipMinPriceCheck = 
      this.config.enableCalculatorOnProduct &&
      this.config.allowCartBuilding &&
      this.isProductPage;
    
    const minValue = skipMinPriceCheck ? 0 : LeaseLinkWidget.MIN_VALUE;
    
    return (
      price >= minValue && price <= LeaseLinkWidget.MAX_VALUE
    );
  }

  async shouldShowCalculator(options?: {
    variationId?: number | string | null;
  }): Promise<boolean> {
    console.log('[LeaseLink] shouldShowCalculator called', {
      isCheckout: this.isCheckout,
      isCart: this.isCart,
      isProductPage: this.isProductPage,
      productId: this.config.productId,
      productType: this.config.productType,
      options
    });

    // 1) Cart page
    if (this.isCart && this.config.enableCalculatorOnCart) {
      try {
        const products = await this.getCartProducts();
        this.products = products;
        const canBuy = products.every((p) => Boolean(p.CanBuy));
        console.log('[LeaseLink] Cart check: canBuy =', canBuy);
        return canBuy;
      } catch (e) {
        console.warn("[LeaseLink] Failed to fetch cart for cart check", e);
        return false;
      }
    }

    if (this.isCheckout) {
      try {
        const products = await this.getCartProducts();
        this.products = products;
        const canBuy = products.every((p) => Boolean(p.CanBuy));
        console.log('[LeaseLink] Checkout check: canBuy =', canBuy);
        return canBuy;
      } catch (e) {
        console.warn("[LeaseLink] Failed to fetch cart for checkout check", e);
        return false;
      }
    }

    // 2) Product card/page
    if (this.isProductPage && this.config.productId) {
      // If product has variations, require a selected variation id
      if (this.config.productType === ProductType.VARIABLE) {
        const vid = options?.variationId;
        if (!vid) {
          console.log('[LeaseLink] Variable product but no variation selected');
          return false; // no variation selected -> don't show
        }
        try {
          const variants = await this.getProductById(vid);
          this.products = variants;
          const result = variants.every(
            (v) => Boolean(v.CanBuy) && this.isPriceInRange(v)
          );
          console.log('[LeaseLink] Variable product check:', result, variants);
          return result;
        } catch (e) {
          console.warn("LeaseLink: failed to fetch variation product", e);
          return false;
        }
      }

      if (this.config.productType === ProductType.GROUPED) {
        try {
          const products = await this.getProductById(this.config.productId);
          this.products = products;

          if (products.length === 0) {
            console.log('[LeaseLink] Grouped product: no products found');
            return false;
          }

          const productPrice = products.reduce((sum, p) => {
            return sum + (Number(p.UnitNetPrice) || 0) * (p.Quantity || 1);
          }, 0);

          const productName = products.map((p: Product) => p.Name).join(" + ");

          const product = Product.fromJson({
            Name: productName,
            UnitNetPrice: productPrice,
            CanBuy: products.every((p) => Boolean(p.CanBuy)),
          });

          const result = products.every((p) => Boolean(p.CanBuy)) &&
            this.isPriceInRange(product);
          console.log('[LeaseLink] Grouped product check:', result, { productPrice, products });
          return result;
        } catch (e) {
          console.warn("LeaseLink: failed to fetch product", e);
          return false;
        }
      }

      // Product without variations
      try {
        const products = await this.getProductById(this.config.productId);
        this.products = products;
        const result = products.every(
          (p) => Boolean(p.CanBuy) && this.isPriceInRange(p)
        );
        console.log('[LeaseLink] Simple product check:', result, products);
        return result;
      } catch (e) {
        console.warn("LeaseLink: failed to fetch product", e);
        return false;
      }
    }

    console.log('[LeaseLink] shouldShowCalculator: no matching condition');
    return false;
  }

  private async getToken() {
    if (this.token === null) {
      const response = await fetch(`${this.apiUrl}/api/GetToken`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ApiKey: this.config.apiKey }),
      });
      const data = await response.json();

      this.token = data.Token;
    }
    return this.token;
  }

  private async createCalculation(items: Product[] = [], headers?: any) {
    try {
      const token = await this.getToken();
      const data = {
        MultiOffer: 0,
        isCartReadOnly: Number(this.isCartReadOnly),
        Items: items.map((p) => Product.toRequestBody(p)),
      };
      const response = await fetch(
        `${this.apiUrl}/api/CreateCalculation`,
        {
          method: "POST",
          headers: Object.assign(
            {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            headers || {}
          ),
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error("Error creating calculation:", error);
      throw error;
    }
  }

  private isLeaseLinkPaymentSelected(): boolean {
    if (!this.isCheckout) return true; // poza checkout nie warunkujemy
    try {
      const selector = this.getPaymentMethodSelector();
      const inputs = Array.from(
        document.querySelectorAll<HTMLInputElement>(selector)
      );
      return inputs.some(
        (el) =>
          el.value === "leaselink_payment" && el.checked
      );
    } catch {
      return false;
    }
  }

  private getPaymentMethodInputNames(): string[] {
    const names = this.config?.paymentMethodInputNames;
    if (Array.isArray(names) && names.length > 0) {
      return names;
    }
    return ["radio-control-wc-payment-method-options", "payment_method"];
  }

  private getPaymentMethodSelector(): string {
    return this.getPaymentMethodInputNames()
      .map((name) => `input[name="${name}"]`)
      .join(", ");
  }

  /**
   * Initialize Calculate Button on product page
   */
  private async initCalculateButton(): Promise<void> {
    if (!this.config.calculateButtonEnabled || !this.isProductPage) {
      return;
    }

    const renderingSelector = this.config.renderToSelector;
    if (!renderingSelector) {
      console.warn("[LeaseLink] Calculate button rendering selector not configured");
      return;
    }

    // Wait for the target element to be available
    const targetElement = await this.waitForElement(renderingSelector);
    if (!targetElement) {
      console.warn(`[LeaseLink] Target element not found: ${renderingSelector}`);
      return;
    }

    // Extract style number from style string (e.g., "style_1" -> "1")
    // Use buttonStyle as fallback if calculateButtonStyle is not set
    const buttonStyleValue = this.config.calculateButtonStyle || this.config.buttonStyle || 'style_1';
    const styleMatch = buttonStyleValue.match(/\d+/);
    const styleNumber = styleMatch ? styleMatch[0] : "1";

    // Map size to button size attribute
    const sizeMap: Record<string, string> = {
      'small': 'small',
      'standard': '',
      'large': 'large'
    };
    const sizeN = sizeMap[this.config.buttonSize || 'standard'] || '';

    // Create button element using custom element
    this.calculateButton = document.createElement('widget-leaselink-button') as WidgetLeaseLinkButton;
    this.calculateButton.setAttribute('data-label', 'Weź w Leasing');
    this.calculateButton.setAttribute('data-style-n', styleNumber);
    if (sizeN) {
      this.calculateButton.setAttribute('data-size-n', sizeN);
    }
    this.calculateButton.setAttribute('data-href', '#');
    this.calculateButton.setAttribute('data-target', '_blank');
    this.calculateButton.dataset.type = this.config.calculateButtonType || 'dynamic';
    
    // Set button as disabled initially until calculation URL is fetched
    this.calculateButton.setDisabled(true);

    // Insert button after target element
    if (targetElement.nextSibling) {
      targetElement.parentNode?.insertBefore(this.calculateButton, targetElement.nextSibling);
    } else {
      targetElement.parentNode?.appendChild(this.calculateButton);
    }

    // Register variation handlers for variable products
    if (this.config.productType === ProductType.VARIABLE) {
      jQuery("form.variations_form").on(
        "show_variation",
        (_: any, variation: any) => void this.handleCalculateButtonVariationChange(variation)
      );
      jQuery("form.variations_form").on(
        "hide_variation",
        () => void this.handleCalculateButtonVariationChange(null)
      );
    }

    // Set up price observer if dynamic price selector is configured
    if (this.config.calculateButtonPriceSelector) {
      this.setupPriceObserver();
    }

    // Initial URL setup
    await this.updateCalculateButtonUrl();
  }

  /**
   * Wait for element to appear in DOM
   */
  private waitForElement(selector: string, timeout: number = 5000): Promise<Element | null> {
    return new Promise((resolve) => {
      const element = document.querySelector(selector);
      if (element) {
        resolve(element);
        return;
      }

      const observer = new MutationObserver(() => {
        const element = document.querySelector(selector);
        if (element) {
          observer.disconnect();
          resolve(element);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      // Timeout fallback
      setTimeout(() => {
        observer.disconnect();
        resolve(null);
      }, timeout);
    });
  }

  /**
   * Set up observer for price changes
   */
  private setupPriceObserver(): void {
    if (!this.config.calculateButtonPriceSelector) {
      return;
    }

    const priceElement = document.querySelector(this.config.calculateButtonPriceSelector);
    if (!priceElement) {
      console.warn(`[LeaseLink] Price element not found: ${this.config.calculateButtonPriceSelector}`);
      return;
    }

    this.priceObserver = new MutationObserver(async () => {
      await this.updateCalculateButtonUrl();
    });

    this.priceObserver.observe(priceElement, {
      childList: true,
      subtree: true,
      characterData: true
    });

    // Also observe parent elements that might update
    const parentElement = priceElement.parentElement;
    if (parentElement) {
      this.priceObserver.observe(parentElement, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }
  }

  /**
   * Build static calculator URL with product data
   */
  private buildStaticCalculatorUrl(product: Product): string {
    const baseUrl = 'https://online.leaselink.pl/RateCalculator/calculate';
    const params = new URLSearchParams();
    
    // Obligatory parameters
    params.append('rate', '999');
    params.append('externalID', this.config.externalId || '');
    params.append('productName', product.Name);
    
    // Optional parameters
    if (product.CategoryLevel1) {
      params.append('categoryLevel', product.CategoryLevel1);
    }
    if (product.CategoryLevel2) {
      params.append('categoryLevel2', product.CategoryLevel2);
    }
    if (product.CategoryLevel3) {
      params.append('categoryLevel3', product.CategoryLevel3);
    }
    if (product.Tax) {
      params.append('tax', String(product.Tax));
    }
    
    // Determine if price is net or gross based on WooCommerce tax settings
    // Default to gross price (isNet=false) as most common scenario
    const useNetPrice = false; // Can be made configurable if needed
    const value = useNetPrice ? product.UnitNetPrice : product.UnitGrossPrice;
    
    params.append('value', String(value));
    params.append('isNet', String(useNetPrice));
    
    if (product.Quantity && product.Quantity > 1) {
      params.append('quantity', String(product.Quantity));
    }
    
    // Financial product type is optional (0 - Leasing, 2 - Raty dla Firm, 10 - Płatność odroczona)
    // Leave it empty to let user choose on calculator page
    
    return `${baseUrl}?${params.toString()}`;
  }

  /**
   * Fetch calculation from WordPress endpoint
   */
  private async fetchCalculation(productId: number, variationId?: number | null): Promise<{ url: string; label: string } | null> {
    try {
      const body: any = { productId };
      if (variationId) {
        body.variationId = variationId;
      }

      const res = await fetch('/wp-json/leaselink/v1/calculate', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => null);
        console.warn('[LeaseLink] Calculate endpoint error:', res.status, errorData);
        return null;
      }

      const data = await res.json();

      // Check if product is eligible for leasing
      if (data.eligible === false) {
        console.log('[LeaseLink] Product not eligible for leasing:', data.reason, data.message);
        return null;
      }

      return {
        url: data.url,
        label: data.label || 'Weź w Leasing',
      };
    } catch (e) {
      console.warn('[LeaseLink] Failed to fetch calculation', e);
      return null;
    }
  }

  /**
   * Update calculate button URL and label with calculation data
   */
  private async updateCalculateButtonUrl(): Promise<void> {
    if (!this.calculateButton) {
      return;
    }

    // Show loading state while fetching calculation
    this.calculateButton.setDisabled(true);
    this.calculateButton.setLoading(true);

    try {
      // Determine product and variation IDs
      let productId: number | null = null;
      let variationId: number | null = null;

      if (this.products.length > 0) {
        // For selected variation
        const product = this.products[0];
        variationId = product.Id ?? null;
      } else if (this.config.productId) {
        // For simple product or no variation selected
        if (this.config.productType === ProductType.VARIABLE) {
          // Variable product without selected variation - hide button
          this.calculateButton.setLoading(false);
          this.calculateButton.hide();
          this.calculateButton.setDisabled(true);
          return;
        }
        productId = parseInt(this.config.productId, 10);
      }

      if (!productId && !variationId) {
        this.calculateButton.setLoading(false);
        this.calculateButton.hide();
        this.calculateButton.setDisabled(true);
        return;
      }

      // Check if we should use static or dynamic button
      const isStaticButton = this.config.calculateButtonType === 'static';

      if (isStaticButton) {
        // Static button: build URL directly from product data
        let product: Product | null = null;

        // Try to get product from already loaded products
        if (this.products.length > 0) {
          product = this.products[0];
        } else {
          // Fetch product data if not already available
          const products = await this.getProductById(productId!);
          if (products.length > 0) {
            product = products[0];
          }
        }

        if (!product) {
          this.calculateButton.setLoading(false);
          this.calculateButton.hide();
          this.calculateButton.setDisabled(true);
          return;
        }

        // Build static calculator URL
        const staticUrl = this.buildStaticCalculatorUrl(product);

        // Set calculator URL
        this.calculateButton.setAttribute('data-href', staticUrl);

        // Update button text
        this.calculateButton.updateButtonText('Weź w Leasing');

        // Enable button
        this.calculateButton.setLoading(false);
        this.calculateButton.setDisabled(false);
        this.calculateButton.show();

      } else {
        // Dynamic button: fetch calculation from backend
        const calculation = await this.fetchCalculation(
          productId || variationId!,
          this.config.productType === ProductType.VARIABLE && variationId ? variationId : null
        );

        if (!calculation || !calculation.url) {
          this.calculateButton.setLoading(false);
          this.calculateButton.hide();
          this.calculateButton.setDisabled(true);
          return;
        }

        // Set calculator URL
        this.calculateButton.setAttribute('data-href', calculation.url);

        // Update button text with label from backend
        this.calculateButton.updateButtonText(calculation.label);

        // Enable button after successful fetch
        this.calculateButton.setLoading(false);
        this.calculateButton.setDisabled(false);
        this.calculateButton.show();
      }
    } catch (e) {
      console.warn('[LeaseLink] Failed to update calculate button URL', e);
      this.calculateButton.setLoading(false);
      this.calculateButton.setDisabled(true);
      this.calculateButton.hide();
    }
  }
}
